import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge } from 'antd';
import {
  CarOutlined, WalletOutlined, ShareAltOutlined, MenuOutlined, BarChartOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import axios from 'axios';
import config from '../../../config';
import usePermissions from '../../../hooks/usePermissions';
// Réutilise le style + le media-query mobile-only + la safe-area de la barre du
// cockpit (masquée ≥701px). Le reste (badge, masquage wizard) est dans livreurNav.css.
import '../../mobileCockpit/components/mobile/bottomNavigation.css';
import './livreurNav.css';

/**
 * Barre de navigation basse du livreur (Phase A refonte navigation). Le livreur
 * était coincé avec une seule barre du haut surchargée : cette barre déplace les
 * accès principaux en bas (pouce), gardés par PERMISSIONS.
 *
 * - Livraisons (accueil/hub) porte le badge « à prendre » + la vibration à
 *   l'arrivée d'une nouvelle mission — repris de la 2ᵉ cloche de la navbar, qui
 *   disparaît (fin de la double cloche).
 * - « Plus » ouvre le tiroir (événement `ndoe:livreur-menu`, cf. LivreurDrawer).
 * - Masquée pendant un wizard (voir livreurNav.css : body:has(.dw-cta)).
 */
export default function LivreurBottomNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { ready, can } = usePermissions();
  const currentUser = useSelector((s) => s.user.currentUser);
  const domain = config.REACT_APP_SERVER_DOMAIN;

  const [pending, setPending] = useState(0);
  const pendingRef = useRef(0); // dernier compte connu (comparaison synchrone, sans re-render)
  const firstLoad = useRef(true);

  // Compteur « à prendre » + vibration (déplacé depuis PageLivreurNavbar : même
  // endpoint, même intervalle 90 s, même seuil de vibration à la hausse).
  useEffect(() => {
    const vibrate = () => { if (navigator.vibrate) navigator.vibrate([180, 80, 180]); };
    const load = async () => {
      try {
        const r = await axios.get(`${domain}/api/mobile-delivery/mine`, {
          params: { scope: 'take-charge' },
          headers: { Authorization: `Bearer ${currentUser?.accessToken || currentUser?.token || ''}` },
        });
        const count = Number(r.data?.summary?.a_prendre || 0);
        // Garde évaluée SYNCHRONIQUEMENT contre le dernier compte (ref stable) —
        // pas dans l'updater de setPending, sinon le batching React 18 exécute
        // `firstLoad.current = false` AVANT la comparaison et fait vibrer au 1er
        // chargement même avec des missions déjà en attente (parité navbar).
        if (!firstLoad.current && count > pendingRef.current) vibrate();
        firstLoad.current = false;
        pendingRef.current = count;
        setPending(count);
      } catch (_) { pendingRef.current = 0; setPending(0); }
    };
    load();
    const id = window.setInterval(load, 90000);
    return () => window.clearInterval(id);
  }, [currentUser?.accessToken, currentUser?.token, domain]);

  const actif = (to) => pathname === to || pathname.startsWith(`${to}/`);
  const go = (to) => { if (!actif(to)) navigate(to); };

  // Droits pas encore chargés : on ne rend pas la barre (évite le passage 2→4
  // onglets qui décale les positions sous le pouce). Idiome de GlobalBottomNav.
  if (!ready) return null;

  // Livraisons + Plus toujours présents ; Dépenses/Catalogue selon permission.
  return (
    <nav className="mobile-action-bottom-nav livreur-bottom-nav" aria-label="Navigation livreur">
      <button type="button" className={actif('/') ? 'is-active' : ''} aria-current={actif('/') ? 'page' : undefined} onClick={() => go('/')}>
        <Badge count={pending} size="small" offset={[2, -2]}><CarOutlined /></Badge>
        <span>Livraisons</span>
      </button>
      <button type="button" className={actif('/mon-activite') ? 'is-active' : ''} aria-current={actif('/mon-activite') ? 'page' : undefined} onClick={() => go('/mon-activite')}>
        <BarChartOutlined /><span>Activité</span>
      </button>
      {can('depenses.declarer') && (
        <button type="button" className={actif('/ma-feuille-depense') ? 'is-active' : ''} aria-current={actif('/ma-feuille-depense') ? 'page' : undefined} onClick={() => go('/ma-feuille-depense')}>
          <WalletOutlined /><span>Dépenses</span>
        </button>
      )}
      {can('partage.voir') && (
        <button type="button" className={actif('/partage-catalogue') ? 'is-active' : ''} aria-current={actif('/partage-catalogue') ? 'page' : undefined} onClick={() => go('/partage-catalogue')}>
          <ShareAltOutlined /><span>Catalogue</span>
        </button>
      )}
      <button type="button" onClick={() => window.dispatchEvent(new Event('ndoe:livreur-menu'))}>
        <MenuOutlined /><span>Plus</span>
      </button>
    </nav>
  );
}
